import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Wrench,
  ClipboardList,
  ShieldCheck,
  HardDrive,
  Vault,
  Users,
  Plus,
  Trash2,
} from "lucide-react";
import * as clientsApi from "../../api/clients";
import { AttivitaDiSede } from "../../api/clients";
import { STATO_LABELS, modelloLeggibile } from "../../api/devices";
import { coloreStato } from "../Devices/Devices";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Badge } from "../../components/ui/Badge";
import { LinkIndietro } from "../../components/ui/LinkIndietro";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { SECTION_COLORS, SectionKey } from "../../utils/sectionColors";
import { DataStorico } from "../../components/ui/DataStorico";
import { isFutura } from "../../utils/dataIpotesi";
import { RiquadroSede } from "../StatoLavori/RiquadroSede";
import { UtentiConti } from "./UtentiConti";
import { etichettaMatricola } from "../../utils/matricola";

/** Data leggibile, oppure "—" se manca. */
function dataIT(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

/**
 * Come si legge la provenienza delle coordinate. `manuale` è scritta al
 * positivo — è l'unico caso in cui qualcuno ha verificato dove sta davvero la
 * sede, e vale più delle altre due, non meno.
 */
const PRECISIONE_LABEL: Record<clientsApi.GeoPrecisione, string> = {
  indirizzo: "Trovata dall'indirizzo",
  comune: "Centro del comune — indicativa",
  manuale: "Inserita a mano",
};

/** Righe di un gruppo: sempre la stessa forma, cambia solo cosa ci si mette dentro. */
interface RigaStorico {
  id: string;
  titolo: string;
  data: string | null;
  /**
   * true se `data` viene dal campo della data PREVISTA e non di quella
   * avvenuta. Senza questo flag la cascata `data_effettiva ?? ipotesi_data`
   * butta via proprio l'informazione che uno storico deve dare — se la cosa è
   * successa davvero. Vedi `components/ui/DataStorico.tsx`.
   */
  dataStimata: boolean;
  /**
   * true solo per le installazioni: `ipotesi_data` è l'unica colonna in cui il
   * primo del mese è un segnaposto di mese e non un giorno. Vedi DataStorico.
   */
  daIpotesiData?: boolean;
  stato: string | null;
  dettaglio: string | null;
}

/**
 * Posizione della sede sulla mappa, con la possibilità di inserirla a mano.
 *
 * ## Perché serve
 *
 * Il geocoding automatico non arriva dappertutto: in produzione circa una sede
 * su tre finisce al centro del comune perché l'indirizzo non è risolvibile, e
 * alcune non si posizionano affatto per refusi in anagrafica. Finora l'unico
 * rimedio era correggere l'indirizzo e sperare che Nominatim lo trovasse. Chi
 * quel posto lo conosce ora può semplicemente dire dov'è.
 *
 * ## Non è una funzione di un modulo
 *
 * Le coordinate della sede alimentano TUTTI i livelli della mappa
 * multi-livello — installazioni, sopralluoghi, interventi, attivazioni — oltre
 * alla mappa Wink On Cash. Correggerne una le sistema tutte insieme, ed è il
 * motivo per cui questo pannello sta sulla scheda Sede e non dentro una delle
 * mappe.
 *
 * Le coordinate messe a mano non verranno sovrascritte da una nuova corsa del
 * geocoding di massa: quello script riprende solo le sedi con `lat` o `lng`
 * nulle.
 */
function PosizioneSede({
  sede,
  onSalvata,
}: {
  sede: clientsApi.SedeConPosizione;
  onSalvata: (sede: clientsApi.SedeConPosizione) => void;
}) {
  const { showToast } = useToast();
  const [lat, setLat] = useState(sede.lat === null ? "" : String(sede.lat));
  const [lng, setLng] = useState(sede.lng === null ? "" : String(sede.lng));
  const [isSaving, setIsSaving] = useState(false);

  const invariate = lat === (sede.lat === null ? "" : String(sede.lat)) && lng === (sede.lng === null ? "" : String(sede.lng));

  async function salva() {
    setIsSaving(true);
    try {
      const { sede: aggiornata } = await clientsApi.salvaPosizioneManuale(sede.id, lat, lng);
      showToast("Posizione salvata: la sede è ora dove l'hai indicata.", "success");
      onSalvata(aggiornata);
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile salvare la posizione."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="mb-8 rounded-card border border-line bg-white p-5 shadow-card">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-ink/50">Posizione sulla mappa</h2>
        <span className="text-xs text-ink/45">
          {sede.geo_precisione ? PRECISIONE_LABEL[sede.geo_precisione] : "Mai geolocalizzata"}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="sede-lat" className="text-xs text-ink/60">
            Latitudine
          </label>
          <Input id="sede-lat" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="es. 44,4056" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="sede-lng" className="text-xs text-ink/60">
            Longitudine
          </label>
          <Input id="sede-lng" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="es. 8,9463" />
        </div>
        <Button variant="secondary" onClick={salva} loading={isSaving} disabled={!lat || !lng || invariate}>
          <MapPin className="h-4 w-4" aria-hidden="true" />
          Salva posizione manuale
        </Button>
      </div>

      <p className="mt-2 text-xs text-ink/45">
        Si copiano da Google Maps con un click destro sul punto esatto. La virgola decimale va bene.
      </p>
    </section>
  );
}

/**
 * Chi può versare nella cassaforte di questa sede.
 *
 * È l'elenco che l'art. 5 del Contratto di Cash Management chiede di
 * comunicare alla società di trasporto valori, e nella Scheda Cliente reale sta
 * sul foglio del singolo punto vendita — per questo vive qui e non sulla scheda
 * del cliente: la cassaforte sta in un negozio preciso, e chi lavora in uno non
 * è per questo autorizzato nell'altro.
 *
 * Nessuna di queste persone è un utente dell'app: sono dipendenti del cliente.
 */
function OperatoriVersamento({ sedeId, canEdit }: { sedeId: string; canEdit: boolean }) {
  const { showToast } = useToast();
  const [operatori, setOperatori] = useState<clientsApi.OperatoreVersamento[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    clientsApi
      .listOperatoriVersamento(sedeId)
      .then((r) => {
        if (!annullato) setOperatori(r.operatori);
      })
      .catch((err) => {
        if (!annullato) showToast(getErrorMessage(err, "Impossibile caricare gli operatori."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sedeId]);

  async function aggiungi() {
    setIsSaving(true);
    try {
      const { operatore } = await clientsApi.createOperatoreVersamento({
        sede_id: sedeId,
        nome: nome.trim(),
        cognome: cognome.trim(),
        note: note.trim() || undefined,
      });
      // Riordinato per cognome come fa il server: senza, la persona appena
      // aggiunta comparirebbe in fondo e sembrerebbe finita in un elenco
      // diverso da quello che si rilegge ricaricando la pagina.
      setOperatori((precedenti) =>
        [...precedenti, operatore].sort((a, b) =>
          a.cognome.localeCompare(b.cognome, "it") || a.nome.localeCompare(b.nome, "it")
        )
      );
      setNome("");
      setCognome("");
      setNote("");
      showToast("Operatore aggiunto.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile aggiungere l'operatore."), "error");
    } finally {
      setIsSaving(false);
    }
  }

  async function rimuovi(op: clientsApi.OperatoreVersamento) {
    const confermato = window.confirm(
      `Togliere ${op.cognome} ${op.nome} dagli autorizzati al versamento di questa sede?`
    );
    if (!confermato) return;
    setEliminandoId(op.id);
    try {
      await clientsApi.deleteOperatoreVersamento(op.id);
      setOperatori((precedenti) => precedenti.filter((o) => o.id !== op.id));
      showToast("Operatore rimosso.", "success");
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile rimuovere l'operatore."), "error");
    } finally {
      setEliminandoId(null);
    }
  }

  return (
    <section className="mb-8 rounded-card border border-line bg-white p-5 shadow-card">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink/50">
          <Users className="h-4 w-4" aria-hidden="true" />
          Autorizzati al versamento
        </h2>
        <span className="text-xs text-ink/45">{operatori.length}</span>
      </div>

      {isLoading ? (
        <p className="py-4 text-center text-sm text-ink/50">Caricamento…</p>
      ) : operatori.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-4 py-4 text-center text-sm text-ink/45">
          Nessuna persona autorizzata a versare nella cassaforte di questa sede.
        </p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line">
          {operatori.map((op) => (
            <li key={op.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">
                  {op.cognome} {op.nome}
                </p>
                {op.note && <p className="mt-0.5 text-xs text-ink/45">{op.note}</p>}
              </div>
              {canEdit && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={eliminandoId === op.id}
                  onClick={() => rimuovi(op)}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                  Rimuovi
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="mt-3 flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-line p-3">
          <div className="min-w-[140px] flex-1">
            <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>
          <div className="min-w-[140px] flex-1">
            <Input label="Cognome" value={cognome} onChange={(e) => setCognome(e.target.value)} />
          </div>
          <div className="min-w-[180px] flex-[2]">
            <Input label="Note" value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {/* Disabilitato finché mancano nome o cognome: mezzo nominativo non è
              comunicabile al vettore, ed è la stessa regola che applica la
              rotta — qui evita solo il viaggio inutile. */}
          <Button
            type="button"
            loading={isSaving}
            disabled={!nome.trim() || !cognome.trim()}
            onClick={aggiungi}
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            Aggiungi
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Vista Sede — cosa è successo, e cosa sta per succedere, presso un singolo
 * punto vendita.
 *
 * È un livello sotto la vista aggregata per cliente: quella risponde "cosa
 * riguarda questa azienda", questa "cosa riguarda questo indirizzo". La
 * distinzione conta per i clienti con decine di punti vendita, dove sapere che
 * un'insegna ha avuto tre interventi non dice se sono stati tutti nello stesso
 * negozio o sparsi su tre città.
 *
 * Raggiungibile dal popup della mappa e dall'elenco sedi in anagrafica: sono i
 * due modi in cui si arriva alla domanda — dalla geografia o dal cliente.
 */
export default function SedeDetail() {
  const { id } = useParams<{ id: string }>();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [dati, setDati] = useState<AttivitaDiSede | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let annullato = false;
    setIsLoading(true);
    clientsApi
      .getAttivitaSede(id)
      .then((d) => {
        if (!annullato) setDati(d);
      })
      .catch((err) => {
        if (!annullato) showToast(getErrorMessage(err, "Impossibile caricare la sede."), "error");
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /**
   * Le date su cui si ragiona. Ogni modulo ne ha due e quella che conta è
   * sempre la più "avvenuta": un intervento completato il 3 è successo il 3,
   * non quando era stato previsto.
   *
   * Su interventi, sopralluoghi e attivazioni è la stessa cascata del
   * Calendario unificato, e tenerla identica evita che la stessa attività
   * risulti in due giorni diversi a seconda di dove la si guarda. Sulle
   * installazioni **diverge di proposito**: il Calendario mostra
   * `ipotesi_data` perché serve a pianificare, qui vale `data_effettiva`
   * quando c'è, perché uno storico racconta quando le cose sono successe.
   */
  const gruppi = useMemo(() => {
    if (!dati) return [];
    const perData = (a: RigaStorico, b: RigaStorico) => {
      if (a.data && b.data) return a.data < b.data ? 1 : -1;
      return a.data ? -1 : b.data ? 1 : 0;
    };

    return [
      {
        chiave: "installazioni" as SectionKey,
        etichetta: "Installazioni",
        icona: HardDrive,
        rotta: "/schedule",
        righe: dati.installazioni
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.insegna ?? "Installazione",
            // A differenza del Calendario, che mostra la data pianificata
            // perché serve a pianificare, qui vale quella effettiva quando
            // c'è: uno storico racconta quando le cose sono successe, non
            // quando erano state previste.
            data: r.data_effettiva ?? r.ipotesi_data,
            // Ha vinto il fallback: la data è quella ipotizzata, non un fatto.
            // Vale anche quando è passata — un'installazione prevista a giugno
            // e mai confermata resta una previsione, non diventa un fatto
            // perché nel frattempo giugno è passato.
            dataStimata: !r.data_effettiva,
            daIpotesiData: true,
            stato: r.stato,
            dettaglio: r.installatore ? `Installatore: ${r.installatore}` : null,
          }))
          .sort(perData),
      },
      {
        chiave: "interventi" as SectionKey,
        etichetta: "Interventi",
        icona: Wrench,
        rotta: "/interventions",
        righe: dati.interventi
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.nome ?? r.cliente_nome ?? "Intervento",
            data: r.data_completamento ?? r.data_prevista,
            dataStimata: !r.data_completamento,
            stato: r.stato,
            dettaglio: r.tecnico ? `Tecnico: ${r.tecnico}` : null,
          }))
          .sort(perData),
      },
      {
        chiave: "sopralluoghi" as SectionKey,
        etichetta: "Sopralluoghi",
        icona: ClipboardList,
        rotta: "/sopralluoghi",
        righe: dati.sopralluoghi
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.ragione_sociale_cliente ?? r.insegna_cliente ?? "Sopralluogo",
            data: r.data_sopralluogo,
            // I sopralluoghi hanno UN campo solo (`data_sopralluogo`, NOT
            // NULL): la cascata non c'è, quindi il campo non dice se è un
            // fatto o una previsione. L'unica cosa che lo distingue è il
            // calendario — è una previsione finché quel giorno non arriva.
            dataStimata: isFutura(r.data_sopralluogo),
            stato: r.stato,
            dettaglio: null,
          }))
          .sort(perData),
      },
      {
        chiave: "attivazioni" as SectionKey,
        etichetta: "Attivazioni",
        icona: ShieldCheck,
        rotta: "/activations",
        righe: dati.attivazioni
          .map<RigaStorico>((r) => ({
            id: r.id,
            titolo: r.cliente_nome ?? "Attivazione",
            data: r.data_effettiva ?? r.data_attivazione,
            dataStimata: !r.data_effettiva,
            stato: r.stato,
            dettaglio: r.tecnico_attivazione ? `Tecnico: ${r.tecnico_attivazione}` : null,
          }))
          .sort(perData),
      },
    ];
  }, [dati]);

  /**
   * "Ultimo intervento" = il più recente fra quelli già avvenuti. Un intervento
   * programmato fra due settimane non è l'ultimo intervento: è la prossima
   * attività, ed è la metrica accanto.
   */
  const ultimoIntervento = useMemo(() => {
    if (!dati) return null;
    const adesso = Date.now();
    const passate = dati.interventi
      .map((r) => r.data_completamento ?? r.data_prevista)
      .filter((d): d is string => Boolean(d) && new Date(d as string).getTime() <= adesso)
      .sort();
    return passate.length ? passate[passate.length - 1] : null;
  }, [dati]);

  /**
   * La data futura più vicina fra i quattro moduli, col modulo a cui
   * appartiene. Le installazioni sono incluse insieme agli altri: se l'unica
   * cosa in programma presso una sede è un'installazione fra tre giorni, una
   * card che dicesse "niente in programma" sarebbe semplicemente falsa.
   */
  const prossimaAttivita = useMemo(() => {
    if (!gruppi.length) return null;
    const adesso = Date.now();
    const future = gruppi
      .flatMap((g) => g.righe.map((r) => ({ data: r.data, modulo: g.etichetta, titolo: r.titolo })))
      .filter((x) => x.data && new Date(x.data).getTime() > adesso)
      .sort((a, b) => (a.data! < b.data! ? -1 : 1));
    return future[0] ?? null;
  }, [gruppi]);

  if (isLoading) {
    return <p className="py-12 text-center text-sm text-ink/50">Caricamento…</p>;
  }

  if (!dati) {
    return (
      <div className="py-12 text-center">
        <p className="text-sm text-ink/60">Sede non trovata.</p>
        <LinkIndietro to="/clients" className="mt-2 inline-block text-sm font-semibold text-primary hover:underline">
          Torna alle Anagrafiche
        </LinkIndietro>
      </div>
    );
  }

  const { sede } = dati;
  const indirizzoCompleto = [sede.indirizzo, [sede.cap, sede.citta].filter(Boolean).join(" "), sede.provincia ? `(${sede.provincia})` : null]
    .filter(Boolean)
    .join(", ");

  return (
    <div>
      {/* Alla scheda del cliente, non all'elenco: è da lì che si apre una sede.
          Se si arriva proprio da quella scheda si torna indietro e la si
          ritrova com'era; altrimenti la si apre. Vedi `LinkIndietro`. */}
      <LinkIndietro
        to={sede.cliente ? `/clients/${sede.cliente.id}` : "/map"}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-ink/50 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {sede.cliente ? "Anagrafiche" : "Mappa"}
      </LinkIndietro>

      {/* --- Intestazione ------------------------------------------------------ */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Building2 className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-ink">{sede.nome_sede ?? "Sede senza nome"}</h1>
            {sede.cliente && (
              <p className="mt-0.5 text-sm text-ink/60">
                {sede.cliente.nome}
              </p>
            )}
            <p className="mt-1 flex items-center gap-1.5 text-sm text-ink/50">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {indirizzoCompleto || "Indirizzo non indicato"}
            </p>
            {/* Stessa avvertenza della mappa, nello stesso posto in cui si
                guarda l'indirizzo: se la posizione è al centro del comune, chi
                legge deve saperlo qui e non solo davanti a un pallino. */}
            {sede.geo_precisione === "comune" && (
              <p className="mt-1.5 inline-block rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                Posizione sulla mappa indicativa (centro del comune): l&apos;indirizzo non è stato riconosciuto. Puoi
                correggerla qui sotto.
              </p>
            )}
            {/* Nessuna coordinata: sulla mappa questa sede non compare affatto,
                e con lei non compaiono i suoi interventi, sopralluoghi e
                attivazioni. Va detto qui, dove si può rimediare. */}
            {sede.lat === null && sede.lng === null && (
              <p className="mt-1.5 inline-block rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                Senza coordinate: questa sede non compare sulla mappa, e con lei nemmeno la sua attività.
              </p>
            )}
          </div>
        </div>
      </header>

      {/* --- Riepilogo --------------------------------------------------------- */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Ultimo intervento</p>
          <p className="mt-1 text-xl font-bold text-ink">{dataIT(ultimoIntervento)}</p>
          <p className="mt-0.5 text-xs text-ink/45">
            {ultimoIntervento ? "Il più recente già svolto" : "Nessun intervento registrato"}
          </p>
        </div>

        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Prossima attività</p>
          <p className="mt-1 text-xl font-bold text-ink">{dataIT(prossimaAttivita?.data ?? null)}</p>
          <p className="mt-0.5 text-xs text-ink/45">
            {prossimaAttivita ? prossimaAttivita.modulo : "Niente in programma"}
          </p>
        </div>

        {/* La terza metrica era spenta in attesa del modulo device. Ora ha una
            fonte: i device con una relazione ANCORA APERTA su questa sede. Uno
            zero qui significa "nessuna cassaforte censita qui", che è
            un'informazione — diverso dal "—" di prima, che significava "non lo
            so ancora". */}
        <div className="rounded-card border border-line bg-white p-4 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">Dispositivi</p>
          <p className="mt-1 text-xl font-bold text-ink">{dati.dispositivi.length}</p>
          <p className="mt-0.5 text-xs text-ink/45">
            {dati.dispositivi.length === 0 ? "Nessuno censito qui" : "Attualmente presso questa sede"}
          </p>
        </div>
      </div>

      {/* --- Stato lavori (issue #211) ------------------------------------------
          Le pratiche di questa sede, e «Avvia pratica». Solo admin e staff:
          per gli altri ruoli la scheda resta com'era, e la richiesta non parte. */}
      {hasPermission("stato_lavori:read") && (
        <RiquadroSede
          sedeId={sede.id}
          clienteId={sede.cliente?.id ?? null}
          dispositivi={dati.dispositivi}
          puoAvviare={hasPermission("stato_lavori:update")}
        />
      )}

      {/* --- Posizione --------------------------------------------------------
          Solo per chi può modificare l'anagrafica sedi: correggere una
          coordinata sposta questa sede su tutte le mappe dell'app, non è una
          preferenza di visualizzazione. */}
      {hasPermission("clienti_sedi:update") && (
        <PosizioneSede
          sede={sede}
          onSalvata={(aggiornata) =>
            // Si aggiorna solo la parte di sede che il salvataggio ha toccato:
            // il resto della pagina (storico, dispositivi) non è cambiato e
            // ricaricarlo tutto farebbe sfarfallare l'elenco per due numeri.
            setDati((d) =>
              d
                ? {
                    ...d,
                    sede: {
                      ...d.sede,
                      lat: aggiornata.lat ?? null,
                      lng: aggiornata.lng ?? null,
                      geo_precisione: "manuale",
                    },
                  }
                : d
            )
          }
        />
      )}

      {/* --- Cash: dove vanno i soldi, e chi li può versare ---------------------
          Due riquadri che rispondono alle due domande operative su un punto
          vendita col servizio Wink on Cash, e che finora vivevano solo nella
          Scheda Cliente Excel. Stanno qui e non sulla scheda del cliente
          perché entrambi sono per SEDE: l'IBAN può cambiare da un negozio
          all'altro, e gli autorizzati alla cassaforte sono le persone che
          lavorano in QUESTO negozio. Il conto di accredito sta dentro
          «Utenti autorizzati e conti», accanto alle utenze AZNext e ai conti
          tecnici delle casseforti. */}
      <UtentiConti sedeId={sede.id} />
      <OperatoriVersamento sedeId={sede.id} canEdit={hasPermission("clienti_sedi:update")} />

      {/* --- Dispositivi ------------------------------------------------------- */}
      {/* Sta sopra lo storico e fuori dai gruppi di proposito: quelli sono
          EVENTI datati, ordinati dal più recente. Un dispositivo non è un
          evento, è ciò che si trova qui adesso — metterlo nella stessa lista lo
          avrebbe fatto ordinare per data insieme agli interventi, che non
          vuol dire niente. */}
      <section className="mb-6">
        <div className="mb-2 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Vault className="h-4 w-4" aria-hidden="true" />
          </span>
          <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">Dispositivi</h2>
          <span className="text-sm text-ink/40">{dati.dispositivi.length}</span>
        </div>

        {dati.dispositivi.length === 0 ? (
          <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
            Nessun dispositivo censito presso questa sede.
          </p>
        ) : (
          <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
            {dati.dispositivi.map((d) => {
              const modello = modelloLeggibile(d);
              return (
                <li key={d.id} className="border-b border-line last:border-b-0">
                  <Link
                    to={`/devices/${d.id}`}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 transition-colors hover:bg-primary/5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-ink">{etichettaMatricola(d.matricola)}</p>
                      <p className="truncate text-xs text-ink/50">
                        {[modello ?? "Modello non indicato", d.codice_gdo ? `GDO ${d.codice_gdo}` : null]
                          .filter(Boolean)
                          .join(" — ")}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <Badge color={coloreStato(d.stato)}>{STATO_LABELS[d.stato]}</Badge>
                      <span className="text-sm tabular-nums text-ink/60">{dataIT(d.data_installazione)}</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* --- Storico raggruppato ----------------------------------------------- */}
      <div className="flex flex-col gap-6">
        {gruppi.map((g) => {
          const colore = SECTION_COLORS[g.chiave].base;
          const Icona = g.icona;
          return (
            <section key={g.chiave}>
              <div className="mb-2 flex items-center gap-2">
                <span
                  className="flex h-7 w-7 items-center justify-center rounded-lg"
                  style={{ backgroundColor: colore, color: SECTION_COLORS[g.chiave].textOn }}
                >
                  <Icona className="h-4 w-4" aria-hidden="true" />
                </span>
                <h2 className="text-sm font-bold uppercase tracking-wide text-ink/70">{g.etichetta}</h2>
                <span className="text-sm text-ink/40">{g.righe.length}</span>
              </div>

              {g.righe.length === 0 ? (
                <p className="rounded-card border border-dashed border-line px-4 py-6 text-center text-sm text-ink/45">
                  Nessun record collegato a questa sede.
                </p>
              ) : (
                <ul className="overflow-hidden rounded-card border border-line bg-white shadow-card">
                  {g.righe.map((r) => (
                    <li
                      key={r.id}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-3 last:border-b-0"
                      style={{ borderLeft: `3px solid ${colore}` }}
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{r.titolo}</p>
                        {r.dettaglio && <p className="text-xs text-ink/50">{r.dettaglio}</p>}
                      </div>
                      <div className="flex shrink-0 items-center gap-3">
                        {r.stato && <Badge stato={r.stato}>{r.stato}</Badge>}
                        <DataStorico data={r.data} stimata={r.dataStimata} daIpotesiData={r.daIpotesiData} stato={r.stato} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
